// #177 Depo konumu seçici — react-leaflet + OpenStreetMap.
// Kullanıcı harita üzerinde tıklayarak veya pini sürükleyerek depo konumunu belirler;
// seçilen koordinatlar üst forma (Latitude/Longitude) yazılır ve kayıtla saklanır.
// Adres araması (Nominatim/OSM) ile blank haritada hızlı konumlanır.
// Leaflet CSS index.js'te global yüklü; default marker ikonu bozuk olduğundan DivIcon kullanılır.
import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';

const DEFAULT_CENTER = [39.0, 35.0]; // Türkiye geneli (konum yokken)
const DEFAULT_ZOOM = 6;
const PINNED_ZOOM = 15;

// Teardrop pin (gövde mavi + beyaz merkez nokta). DivIcon → default ikon yol sorununu atlar.
const PIN_ICON = L.divIcon({
  className: 'wh-location-pin',
  html: `<div style="
    width:28px;height:28px;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);
    background:#2563eb;border:3px solid #fff;
    box-shadow:0 3px 8px rgba(15,23,42,0.4);
    display:flex;align-items:center;justify-content:center;">
    <span style="width:8px;height:8px;border-radius:50%;background:#fff;display:block;"></span>
  </div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

// Haritaya tıklanınca konumu set eder.
function ClickToPlace({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

// Programatik konum değişiminde (kayıt yüklenince / arama sonucu) haritayı oraya taşır.
function Recenter({ lat, lng }) {
  const map = useMap();
  const lastRef = useRef(null);
  useEffect(() => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    const key = `${lat},${lng}`;
    if (lastRef.current === key) return;
    lastRef.current = key;
    map.setView([lat, lng], Math.max(map.getZoom(), PINNED_ZOOM));
  }, [lat, lng, map]);
  return null;
}

// Pin yokken haritayı seçili ülkenin görünümüne taşır (ülke değişince de).
// Pin varsa dokunmaz — kullanıcının seçtiği konum ülke değişiminde kaybolmasın.
function CountryView({ center, zoom, active }) {
  const map = useMap();
  const lastRef = useRef(null);
  useEffect(() => {
    if (!active || !Array.isArray(center)) return;
    const key = `${center[0]},${center[1]},${zoom}`;
    if (lastRef.current === key) return;
    lastRef.current = key;
    map.setView(center, zoom);
  }, [center, zoom, active, map]);
  return null;
}

// #808 countryCode (ISO alpha-2, ör. 'tr','cy') opsiyoneldir. Verilirse adres araması o ülkeye
// kısıtlanır; verilmezse GLOBAL arama yapılır (farklı ülkelerdeki adresler de bulunabilir/kaydedilebilir).
// countryCenter/countryZoom verilirse pin yokken harita o ülkeye odaklanır (yoksa Türkiye geneli).
export default function LocationPickerMap({
  lat, lng, onChange, onAddress, height = 380, disabled = false,
  countryCode, countryCenter, countryZoom,
}) {
  const hasPoint = Number.isFinite(lat) && Number.isFinite(lng);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState('');
  // İşletme/adres araması sonuç listesi ("levent kuaför" → aday kuaförler).
  const [results, setResults] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  // Gecikmiş isteklerin güncel sonucu ezmemesi için istek sayacı (son kazanır).
  const reqSeqRef = useRef(0);
  // #266 onAddress verildiyse seçilen konum reverse-geocode edilir, adres forma aktarılır.
  const [resolving, setResolving] = useState(false);
  const [resolvedText, setResolvedText] = useState('');

  // #266 Koordinatı Nominatim reverse endpoint'i ile açık adrese çevirir, üst forma iletir.
  const resolveAddress = useCallback(async (la, ln) => {
    setResolving(true);
    setResolvedText('');
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${la}&lon=${ln}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'tr' } });
      const data = await res.json();
      if (data && !data.error) {
        setResolvedText(data.display_name || '');
        onAddress?.(data);
      } else {
        setResolvedText('Adres çözümlenemedi.');
      }
    } catch {
      setResolvedText('Adres alınamadı.');
    } finally {
      setResolving(false);
    }
  }, [onAddress]);

  const handlePick = useCallback((la, ln) => {
    if (disabled) return;
    // 8 ondalığa yuvarla (DB precision(18,8) ile uyumlu).
    const rla = Number(la.toFixed(8));
    const rln = Number(ln.toFixed(8));
    onChange?.(rla, rln);
    if (onAddress) resolveAddress(rla, rln);
  }, [onChange, disabled, onAddress, resolveAddress]);

  const handleSearch = useCallback(async (e) => {
    e?.preventDefault?.();
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchMsg('');
    try {
      // #808 Ülke kısıtı dinamik: countryCode verilmişse o ülkeye kısıtla, yoksa global ara.
      const cc = String(countryCode || '').trim().toLowerCase();
      const ccParam = cc ? `&countrycodes=${encodeURIComponent(cc)}` : '';
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1${ccParam}&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'tr' } });
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        handlePick(parseFloat(data[0].lat), parseFloat(data[0].lon));
        setSearchMsg('');
      } else {
        setSearchMsg('Adres bulunamadı.');
      }
    } catch {
      setSearchMsg('Arama yapılamadı.');
    } finally {
      setSearching(false);
    }
  }, [query, handlePick, countryCode]);

  const fallbackCenter = Array.isArray(countryCenter) ? countryCenter : DEFAULT_CENTER;
  const fallbackZoom = Number.isFinite(countryZoom) ? countryZoom : DEFAULT_ZOOM;
  const center = useMemo(
    () => (hasPoint ? [lat, lng] : fallbackCenter),
    [hasPoint, lat, lng, fallbackCenter],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* Adres arama + koordinat rozeti */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {!disabled && (
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 6, flex: '1 1 280px', minWidth: 220 }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Adres ara (örn. mahalle, ilçe, il)…"
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 8, fontSize: 13,
                border: '1px solid var(--border-color, #cbd5e1)', background: 'var(--input-bg, #fff)',
                color: 'var(--text-primary, #0f172a)',
              }}
            />
            <button type="submit" className="dps-btn ghost" disabled={searching || !query.trim()}>
              {searching ? 'Aranıyor…' : 'Ara'}
            </button>
          </form>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
          {hasPoint ? (
            <span style={{
              fontSize: 12, fontVariantNumeric: 'tabular-nums', color: 'var(--text-secondary, #475569)',
              background: 'var(--surface-2, #f1f5f9)', borderRadius: 8, padding: '6px 10px', whiteSpace: 'nowrap',
            }}>
              📍 {Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)' }}>
              Konum seçilmedi
            </span>
          )}
          {hasPoint && !disabled && (
            <button type="button" className="dps-btn ghost" onClick={() => onChange?.(null, null)}>
              Temizle
            </button>
          )}
        </div>
      </div>

      {searchMsg && (
        <div style={{ fontSize: 12, color: '#b45309' }}>{searchMsg}</div>
      )}

      {/* #266 Reverse-geocode durumu: çözümlenen açık adres (Getir/Migros benzeri). */}
      {onAddress && (resolving || resolvedText) && (
        <div style={{
          fontSize: 12, lineHeight: 1.4,
          color: resolving ? 'var(--text-secondary, #475569)' : '#0f766e',
          background: 'var(--surface-2, #f0fdfa)', border: '1px solid var(--border-color, #ccfbf1)',
          borderRadius: 8, padding: '6px 10px',
        }}>
          {resolving ? '📍 Adres çözümleniyor…' : `📌 ${resolvedText}`}
        </div>
      )}

      <div style={{ position: 'relative', borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border-color, #e5e7eb)' }}>
        <MapContainer center={center} zoom={hasPoint ? PINNED_ZOOM : fallbackZoom} style={{ height, width: '100%' }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Recenter lat={lat} lng={lng} />
          <CountryView center={fallbackCenter} zoom={fallbackZoom} active={!hasPoint} />
          {!disabled && <ClickToPlace onPick={handlePick} />}
          {hasPoint && (
            <Marker
              position={[lat, lng]}
              icon={PIN_ICON}
              draggable={!disabled}
              eventHandlers={{
                dragend(e) {
                  const p = e.target.getLatLng();
                  handlePick(p.lat, p.lng);
                },
              }}
            />
          )}
        </MapContainer>

        {!disabled && (
          <div style={{
            position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1000,
            background: 'rgba(15,23,42,0.78)', color: '#fff', borderRadius: 999, padding: '5px 14px',
            fontSize: 12, fontWeight: 600, pointerEvents: 'none', whiteSpace: 'nowrap',
          }}>
            {hasPoint ? 'Pini sürükleyerek veya yeniden tıklayarak konumu güncelleyin' : 'Haritaya tıklayarak depo konumunu pinleyin'}
          </div>
        )}
      </div>
    </div>
  );
}
